import { HEX_DIRS, toAxial, fromAxial, hexNeighbors } from '../../../rules/hexGrid'

// ─── Hex board — intel peek geometry ──────────────────────────────────────────
// A bearing "A:5" is drawn as ONE big hexagon: the line through the centres
// of every hex exactly 5 steps from the stamp. Its corners are the centres of
// the six hexes 5 steps straight out along each HEX_DIRS direction, and the
// ring's other hexes sit on the straight edges between them. Corners can fall
// off the Hive; the board clips the drawing to the land.

// Board-space step for each HEX_DIRS direction on one side (the layout is
// linear in axial coordinates, so one interior hex and its neighbours give it).
export function dirVectors(cells) {
    const s0 = cells.findIndex((c, s) => hexNeighbors(s).length === 6)
    const { q, r } = toAxial(s0)
    return HEX_DIRS.map(([dq, dr]) => {
        const n = fromAxial(q + dq, r + dr)
        return { x: cells[n].cx - cells[s0].cx, y: cells[n].cy - cells[s0].cy }
    })
}

// The six corners of a bearing's hexagon, in ring order.
export function bearingHexagon(cx, cy, d, dirs) {
    return dirs.map(v => ({ x: cx + d * v.x, y: cy + d * v.y }))
}

const EPS = 0.5     // board units: points closer than this are one point

function segIntersect(a, b, c, d) {
    const rx = b.x - a.x, ry = b.y - a.y
    const sx = d.x - c.x, sy = d.y - c.y
    const den = rx * sy - ry * sx
    if (Math.abs(den) < 1e-9) return null              // parallel (or collinear): no single point
    const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / den
    const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / den
    if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null
    return { x: a.x + t * rx, y: a.y + t * ry }
}

function distToSeg(p, a, b) {
    const vx = b.x - a.x, vy = b.y - a.y
    const len2 = vx * vx + vy * vy
    const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2)) : 0
    return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy))
}

const edges = (pts) => pts.map((p, i) => [p, pts[(i + 1) % pts.length]])
const onOutline = (p, pts) => edges(pts).some(([a, b]) => distToSeg(p, a, b) < EPS)

// Every point where two or more hexagons' outlines cross. Each comes once,
// with every hexagon whose outline passes through it: { x, y, members: [i…] }
// (indices into `hexagons`). Where two outlines run along the same line they
// share a stretch, not a point, and add nothing.
export function hexagonCrossings(hexagons) {
    const points = []
    for (let i = 0; i < hexagons.length; i++) {
        for (let j = i + 1; j < hexagons.length; j++) {
            for (const [a, b] of edges(hexagons[i])) {
                for (const [c, d] of edges(hexagons[j])) {
                    const p = segIntersect(a, b, c, d)
                    if (p && !points.some(q => Math.hypot(q.x - p.x, q.y - p.y) < EPS)) points.push(p)
                }
            }
        }
    }
    return points.map(p => ({ ...p, members: hexagons.map((h, i) => (onOutline(p, h) ? i : -1)).filter(i => i >= 0) }))
        .filter(p => p.members.length >= 2)
}

// Outlines that run along the same stretch of line sit side by side, not on
// top of each other. Every outline is cut into its one-hex steps (a bearing
// of distance d has d steps per side, centre to centre); a step shared by n
// outlines is drawn n times, each pushed sideways off the line so the
// strokes abut — `width` apart, the group centred on the line. Each outline
// takes the side its own stamp (`centres`) is on, so none crosses over
// another: stamps on opposite sides split the strips between them; stamps on
// the same side nest, the smaller hexagon innermost (nearest the stamps).
// Ties keep outline order. A step no other outline uses stays on the line.
// Returns, per hexagon, its steps as [{ x1, y1, x2, y2 }].
export function layeredSteps(hexagons, distances, width, centres = null) {
    const key = (p) => `${Math.round(p.x * 10)},${Math.round(p.y * 10)}`
    const steps = hexagons.map((pts, h) => {
        const d = distances[h]
        const out = []
        pts.forEach((a, k) => {
            const b = pts[(k + 1) % pts.length]
            for (let i = 0; i < d; i++) {
                const p = { x: a.x + (b.x - a.x) * i / d, y: a.y + (b.y - a.y) * i / d }
                const q = { x: a.x + (b.x - a.x) * (i + 1) / d, y: a.y + (b.y - a.y) * (i + 1) / d }
                // One frame per step, whichever way an outline walks it, so
                // every outline sharing it agrees which side is which.
                // (Compared as numbers, so the frame never flips along a line.)
                const before = Math.round(p.x * 10) - Math.round(q.x * 10) || Math.round(p.y * 10) - Math.round(q.y * 10)
                const [m, n] = before < 0 ? [p, q] : [q, p]
                out.push({ p, q, id: `${key(m)}|${key(n)}`, m, n })
            }
        })
        return out
    })
    const users = new Map()
    steps.forEach((list, h) => list.forEach(st => {
        const u = users.get(st.id) ?? []
        if (!u.includes(h)) users.set(st.id, [...u, h])
    }))
    return steps.map((list, h) => list.map(({ p, q, id, m, n }) => {
        const len = Math.hypot(n.x - m.x, n.y - m.y) || 1
        const ux = -(n.y - m.y) / len, uy = (n.x - m.x) / len       // the step's sideways direction
        // Order the step's outlines across the line, along (ux, uy): by the
        // side their stamp is on, then nearest stamp innermost.
        const place = (k) => {
            if (!centres) return 0
            const side = Math.sign((centres[k].x - m.x) * ux + (centres[k].y - m.y) * uy)
            return side * (1e6 - distances[k])
        }
        const u = [...users.get(id)].sort((a, b) => place(a) - place(b) || a - b)
        const shift = (u.indexOf(h) - (u.length - 1) / 2) * width
        const nx = ux * shift, ny = uy * shift
        return { x1: p.x + nx, y1: p.y + ny, x2: q.x + nx, y2: q.y + ny }
    }))
}

// One outline's steps (layeredSteps) as a single closed path with its corners
// rounded. Consecutive steps that turn a corner meet where their (possibly
// shifted) lines cross, so a corner on a shared stretch is a true point and
// rounds like any other; consecutive steps along one line either run straight
// on or, where an outline moves onto or off a shared stretch, jog sideways by
// a short link. Points along a straight run drop out, and every remaining
// turn is eased with a quadratic curve reaching `radius` back along each side
// (less where a side is short).
export function roundedOutline(steps, radius) {
    const same = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) < 0.05
    const n = steps.length
    const pts = []
    for (let i = 0; i < n; i++) {
        const s = steps[i], u = steps[(i + 1) % n]
        const rx = s.x2 - s.x1, ry = s.y2 - s.y1
        const sx = u.x2 - u.x1, sy = u.y2 - u.y1
        const den = rx * sy - ry * sx
        if (Math.abs(den) > 1e-6 * Math.hypot(rx, ry) * Math.hypot(sx, sy)) {
            // A corner: where this step's line meets the next one's.
            const k = ((u.x1 - s.x1) * sy - (u.y1 - s.y1) * sx) / den
            pts.push({ x: s.x1 + k * rx, y: s.y1 + k * ry })
        } else {
            const b = { x: s.x2, y: s.y2 }, a = { x: u.x1, y: u.y1 }
            pts.push(b)
            if (!same(a, b)) pts.push(a)            // a jog onto or off a shared stretch
        }
    }
    // Drop points where the line runs straight on (and repeats).
    const turns = pts.filter((p, i) => {
        const a = pts[(i - 1 + pts.length) % pts.length], b = pts[(i + 1) % pts.length]
        if (same(p, a)) return false
        const cross = (p.x - a.x) * (b.y - p.y) - (p.y - a.y) * (b.x - p.x)
        return Math.abs(cross) > 1e-3 * Math.hypot(p.x - a.x, p.y - a.y) * Math.hypot(b.x - p.x, b.y - p.y)
    })
    return roundedPolygon(turns, radius)
}

// A closed polygon as a path with every corner eased by a quadratic curve
// reaching `radius` back along each side (less where a side is short).
export function roundedPolygon(turns, radius) {
    if (turns.length < 3) return ''
    const f = (n) => n.toFixed(2)
    const toward = (p, q, r) => {
        const len = Math.hypot(q.x - p.x, q.y - p.y) || 1
        return { x: p.x + (q.x - p.x) * r / len, y: p.y + (q.y - p.y) * r / len }
    }
    const n = turns.length
    let d = ''
    turns.forEach((p, i) => {
        const a = turns[(i - 1 + n) % n], b = turns[(i + 1) % n]
        const r = Math.min(radius, Math.hypot(p.x - a.x, p.y - a.y) / 2, Math.hypot(b.x - p.x, b.y - p.y) / 2)
        const in_ = toward(p, a, r), out = toward(p, b, r)
        d += `${i === 0 ? 'M' : 'L'}${f(in_.x)} ${f(in_.y)}Q${f(p.x)} ${f(p.y)} ${f(out.x)} ${f(out.y)}`
    })
    return `${d}Z`
}

// A sonar arm's afterglow as pie slices about (cx, cy), radius `reach`: the
// arm points straight up and turns clockwise, so the glow trails behind it
// (anticlockwise), strongest at the arm. Returns [{ points, opacity }].
export function sonarWedge(cx, cy, reach, { trailDeg = 70, slices = 14, leadDeg = 4 } = {}) {
    const at = (deg) => {
        const t = deg * Math.PI / 180
        return `${(cx + reach * Math.sin(t)).toFixed(2)},${(cy - reach * Math.cos(t)).toFixed(2)}`
    }
    const out = [{ points: `${cx},${cy} ${at(leadDeg)} ${at(0)}`, opacity: 0.6 }]
    const step = trailDeg / slices
    for (let k = 0; k < slices; k++) {
        out.push({ points: `${cx},${cy} ${at(-k * step)} ${at(-(k + 1) * step)}`, opacity: (1 - k / slices) ** 1.6 })
    }
    return out
}
